import { useState, useEffect } from 'react';
import { ChevronLeft, Shield, Eye, Database, Trash2, Download, Bell, Lock, ToggleLeft, ToggleRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PrivacySetting {
    id: string;
    label: string;
    description: string;
    enabled: boolean;
    icon: React.ElementType;
}

const PRIVACY_STORAGE_KEY = 'way2will_privacy_settings';

export default function AppPrivacySettings() {
    const navigate = useNavigate();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [settings, setSettings] = useState<PrivacySetting[]>([
        {
            id: 'analytics',
            label: 'Usage Analytics',
            description: 'Help improve Way2Will by sharing anonymous usage data',
            enabled: true,
            icon: Database,
        },
        {
            id: 'voiceStorage',
            label: 'Voice Recording Storage',
            description: 'Store voice recordings to improve AI accuracy',
            enabled: false,
            icon: Eye,
        },
        {
            id: 'personalizedAI',
            label: 'Personalized AI Suggestions',
            description: 'Allow AI to learn from your habits and preferences',
            enabled: true,
            icon: Shield,
        },
        {
            id: 'emailNotifications',
            label: 'Email Notifications',
            description: 'Receive product updates and tips via email',
            enabled: true,
            icon: Bell,
        },
        {
            id: 'thirdPartySharing',
            label: 'Third-Party Data Sharing',
            description: 'Share data with integration partners for enhanced features',
            enabled: false,
            icon: Lock,
        },
    ]);

    // Load settings from storage
    useEffect(() => {
        const stored = localStorage.getItem(PRIVACY_STORAGE_KEY);
        if (stored) {
            try {
                const savedSettings = JSON.parse(stored);
                setSettings(prev => prev.map(s => ({
                    ...s,
                    enabled: savedSettings[s.id] ?? s.enabled
                })));
            } catch (e) {
                console.error('Error loading privacy settings:', e);
            }
        }
    }, []);

    const handleToggle = (id: string) => {
        setSettings(prev => {
            const updated = prev.map(s =>
                s.id === id ? { ...s, enabled: !s.enabled } : s
            );
            // Save to storage
            const toSave = updated.reduce((acc, s) => ({ ...acc, [s.id]: s.enabled }), {});
            localStorage.setItem(PRIVACY_STORAGE_KEY, JSON.stringify(toSave));
            return updated;
        });
    };

    const handleExportData = () => {
        navigate('/app/profile');
        // The export functionality is in profile settings
    };

    const handleDeleteAccount = () => {
        // This would trigger an email to support
        window.location.href = 'mailto:support@way2will.com?subject=Account%20Deletion%20Request&body=Please%20delete%20my%20Way2Will%20account.%20My%20email%20is%3A%20';
        setShowDeleteConfirm(false);
    };

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                >
                    <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                </button>
                <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Privacy Settings</h1>
            </div>

            {/* Privacy Settings List */}
            <div className="bg-white dark:bg-dark-card rounded-2xl shadow-sm overflow-hidden mb-6">
                {settings.map((setting, index) => {
                    const Icon = setting.icon;
                    return (
                        <div
                            key={setting.id}
                            className={`p-4 flex items-center justify-between ${index < settings.length - 1 ? 'border-b border-[#E5EBF0] dark:border-dark-border' : ''
                                }`}
                        >
                            <div className="flex items-start gap-3 flex-1">
                                <div className="w-10 h-10 rounded-full bg-[#F5F8FA] dark:bg-dark-elevated flex items-center justify-center flex-shrink-0">
                                    <Icon className="w-5 h-5 text-[#0D5F6B] dark:text-dark-teal-light" />
                                </div>
                                <div className="flex-1">
                                    <span className="text-sm font-medium text-[#1A2832] dark:text-white block">
                                        {setting.label}
                                    </span>
                                    <span className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">
                                        {setting.description}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => handleToggle(setting.id)}
                                className="ml-4"
                            >
                                {setting.enabled ? (
                                    <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                                ) : (
                                    <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Data Management Section */}
            <h2 className="text-base font-semibold text-[#1A2832] dark:text-white mb-3">Data Management</h2>
            <div className="bg-white dark:bg-dark-card rounded-2xl shadow-sm overflow-hidden mb-6">
                <button
                    onClick={handleExportData}
                    className="w-full p-4 flex items-center gap-3 hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated transition border-b border-[#E5EBF0] dark:border-dark-border"
                >
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <Download className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div className="flex-1 text-left">
                        <span className="text-sm font-medium text-[#1A2832] dark:text-white block">Export My Data</span>
                        <span className="text-xs text-[#7B8C9B]">Download all your data as JSON or CSV</span>
                    </div>
                </button>
                <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full p-4 flex items-center gap-3 hover:bg-[#FFEAE7] transition"
                >
                    <div className="w-10 h-10 rounded-full bg-[#FFEAE7] flex items-center justify-center">
                        <Trash2 className="w-5 h-5 text-[#D66B5C]" />
                    </div>
                    <div className="flex-1 text-left">
                        <span className="text-sm font-medium text-[#D66B5C] block">Delete My Account</span>
                        <span className="text-xs text-[#7B8C9B]">Permanently delete your account and all data</span>
                    </div>
                </button>
            </div>

            {/* Privacy Links */}
            <div className="text-center">
                <button
                    onClick={() => navigate('/app/privacy')}
                    className="text-sm text-[#0D5F6B] font-medium"
                >
                    View Full Privacy Policy →
                </button>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-6 w-full max-w-sm">
                        <div className="w-14 h-14 bg-[#FFEAE7] rounded-full flex items-center justify-center mx-auto mb-4">
                            <Trash2 className="w-7 h-7 text-[#D66B5C]" />
                        </div>
                        <h2 className="text-xl font-bold text-[#1A2832] dark:text-white text-center mb-2">
                            Delete Account?
                        </h2>
                        <p className="text-sm text-[#7B8C9B] text-center mb-6">
                            This action cannot be undone. All your data, tasks, events, and settings will be permanently deleted.
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={handleDeleteAccount}
                                className="w-full py-3 bg-[#D66B5C] text-white rounded-xl font-semibold"
                            >
                                Yes, Delete My Account
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="w-full py-3 border border-[#DCE4EA] text-[#4A5C6A] rounded-xl font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
